import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Plus, Trash2, UsersRound } from "lucide-react";
import { initials } from "@/lib/meta";
import { toast } from "sonner";

export default function Teams() {
  const { users } = useApp();
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setTeams((await api.get("/teams")).data);
  useEffect(() => { load(); }, []);

  const del = async (t) => {
    if (!window.confirm(`Delete team "${t.name}"?`)) return;
    await api.delete(`/teams/${t.id}`);
    toast.success("Team deleted"); load();
  };

  return (
    <div className="px-6 py-6 max-w-[1200px]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Organization</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Teams</h1>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-team-btn" className="gap-1.5"><Plus className="w-4 h-4" /> New team</Button>
      </div>
      {teams.length === 0 ? (
        <Card className="p-16 text-center">
          <UsersRound className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-display text-lg font-semibold">No teams yet</div>
          <div className="text-sm text-muted-foreground mt-1">Group people into teams to plan capacity together.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {teams.map((t) => {
            const members = (t.member_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
            const lead = users.find((u) => u.id === t.lead_id);
            return (
              <Card key={t.id} className="p-5" data-testid={`team-${t.id}`}>
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-10 h-10 grid place-items-center rounded-md bg-primary/15 text-primary font-mono font-semibold">{(t.name || "T").slice(0, 2).toUpperCase()}</div>
                  <div className="flex-1">
                    <div className="font-display text-lg font-semibold">{t.name}</div>
                    <div className="text-xs text-muted-foreground">{lead ? `Led by ${lead.name}` : "No lead set"} · {members.length} members</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(t); setOpen(true); }} data-testid={`edit-team-${t.id}`}>Edit</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(t)} data-testid={`del-team-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
                {t.description ? <div className="text-sm text-muted-foreground mb-2">{t.description}</div> : null}
                <div className="flex items-center -space-x-2 mt-2">
                  {members.slice(0, 8).map((m) => (
                    <Avatar key={m.id} className="w-7 h-7 border-2 border-background"><AvatarImage src={m.avatar_url} /><AvatarFallback className="text-[10px]">{initials(m.name)}</AvatarFallback></Avatar>
                  ))}
                  {members.length > 8 ? <div className="w-7 h-7 rounded-full bg-secondary text-[10px] grid place-items-center border-2 border-background">+{members.length - 8}</div> : null}
                </div>
              </Card>
            );
          })}
        </div>
      )}
      <TeamDialog open={open} setOpen={setOpen} editing={editing} users={users} onSaved={load} />
    </div>
  );
}

function TeamDialog({ open, setOpen, editing, users, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", lead_id: "none", member_ids: [] });
  useEffect(() => {
    if (open) {
      setForm(editing
        ? { name: editing.name || "", description: editing.description || "", lead_id: editing.lead_id || "none", member_ids: editing.member_ids || [] }
        : { name: "", description: "", lead_id: "none", member_ids: [] });
    }
  }, [open, editing]);

  const toggleMember = (uid) => {
    setForm((f) => ({ ...f, member_ids: f.member_ids.includes(uid) ? f.member_ids.filter((x) => x !== uid) : [...f.member_ids, uid] }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, lead_id: form.lead_id === "none" ? null : form.lead_id };
    if (editing) await api.patch(`/teams/${editing.id}`, payload);
    else await api.post("/teams", payload);
    toast.success(editing ? "Team updated" : "Team created");
    setOpen(false); onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="team-dialog">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Edit team" : "New team"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="td-name" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="td-desc" /></div>
          <div><Label>Lead</Label>
            <Select value={form.lead_id} onValueChange={(v) => setForm({ ...form, lead_id: v })}>
              <SelectTrigger data-testid="td-lead"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— No lead —</SelectItem>
                {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Members</Label>
            <div className="border border-border rounded-md p-2 max-h-48 overflow-y-auto space-y-1">
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-accent cursor-pointer text-sm" data-testid={`td-member-${u.email}`}>
                  <input type="checkbox" checked={form.member_ids.includes(u.id)} onChange={() => toggleMember(u.id)} className="accent-primary" />
                  <Avatar className="w-6 h-6"><AvatarImage src={u.avatar_url} /><AvatarFallback className="text-[10px]">{initials(u.name)}</AvatarFallback></Avatar>
                  <span className="flex-1">{u.name}</span>
                  <span className="text-xs text-muted-foreground">{u.email}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} data-testid="td-save">{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
